import React, { useEffect, useState } from 'react';
import {
  DollarSign,
  Percent,
  Settings,
  Calculator,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import { usePricing } from '@context/PricingContext';
import { useOnboardingReturn } from '@hooks/useOnboardingReturn';
import Button from '@components/common/Button';
import Alert from '@components/common/Alert';
import LoadingSpinner from '@components/common/LoadingSpinner';
import DashboardCard from '@components/dashboard/components/DashboardCard';
import PricingForm from '../components/pricing/PricingForm';
import DiscountTiersEditor from '../components/pricing/DiscountTiersEditor';
import EventTypeMultipliers from '../components/pricing/EventTypeMultipliers';
import PromoCodeManager from '../components/pricing/PromoCodeManager';
import PricingPreview from '../components/pricing/PricingPreview';

const PricingManagement: React.FC = () => {
  const { configuration, loading, error, fetchConfiguration, clearError } = usePricing();
  const returnToOnboarding = useOnboardingReturn();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  useEffect(() => {
    if (!configuration) {
      fetchConfiguration();
    }
  }, [configuration, fetchConfiguration]);

  useEffect(() => {
    if (configuration?.updatedAt) {
      setLastSaved(new Date(configuration.updatedAt));
      setHasUnsavedChanges(false);
    }
  }, [configuration?.updatedAt]);

  const handleDataChange = () => {
    setHasUnsavedChanges(true);
  };

  const handleSaveSuccess = () => {
    setLastSaved(new Date());
    setHasUnsavedChanges(false);
    returnToOnboarding();
  };

  if (loading && !configuration) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 space-y-4">
        <Alert
          type="error"
          title="Failed to load pricing configuration"
          message={error}
        />
        <Button
          variant="primary"
          onClick={() => {
            clearError();
            fetchConfiguration();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!configuration) {
    return (
      <div className="p-6 space-y-4">
        <Alert
          type="warning"
          title="No pricing configuration found"
          message="Please configure your pricing settings to start selling photos."
        />
        <Button variant="primary" onClick={fetchConfiguration}>
          Set Up Pricing
        </Button>
      </div>
    );
  }

  // Calculate some stats for dashboard cards
  const averageDiscountPercent =
    configuration.volumeDiscounts.length > 0
      ? configuration.volumeDiscounts.reduce((sum, d) => sum + d.discountPercent, 0) /
        configuration.volumeDiscounts.length
      : 0;

  const activeOffersCount = configuration.specialOffers.filter(o => o.isActive).length;
  const commissionRate = Math.round(configuration.photographerCommission * 100);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Pricing Management</h1>
          <p className="text-gray-600 mt-2">
            Configure your photo pricing, discounts, and special offers.
          </p>
        </div>

        <div className="flex items-center space-x-4">
          {hasUnsavedChanges && (
            <div className="flex items-center text-amber-600 text-sm">
              <AlertTriangle className="h-4 w-4 mr-1" />
              Unsaved changes
            </div>
          )}

          {lastSaved && !hasUnsavedChanges && (
            <div className="flex items-center text-green-600 text-sm">
              <CheckCircle className="h-4 w-4 mr-1" />
              Last saved {lastSaved.toLocaleTimeString()}
            </div>
          )}

          <Button
            variant="ghost"
            onClick={fetchConfiguration}
            icon={<RefreshCw className="h-4 w-4" />}
            disabled={loading}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <DashboardCard
          title="Base Photo Price"
          value={`$${(configuration.basePrices.singlePhoto / 100).toFixed(2)}`}
          icon={DollarSign}
        />

        <DashboardCard
          title="Avg. Volume Discount"
          value={`${averageDiscountPercent.toFixed(1)}%`}
          icon={Percent}
        />

        <DashboardCard title="Active Promotions" value={activeOffersCount} icon={Settings} />

        <DashboardCard title="Commission Rate" value={`${commissionRate}%`} icon={Calculator} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left Column - Configuration Forms */}
        <div className="xl:col-span-2 space-y-6">
          {/* Base Pricing */}
          <PricingForm onDataChange={handleDataChange} onSaveSuccess={handleSaveSuccess} />

          {/* Volume Discounts */}
          <DiscountTiersEditor onDataChange={handleDataChange} onSaveSuccess={handleSaveSuccess} />

          {/* Event Type Multipliers */}
          <EventTypeMultipliers onDataChange={handleDataChange} onSaveSuccess={handleSaveSuccess} />

          {/* Promo Codes */}
          <PromoCodeManager onDataChange={handleDataChange} onSaveSuccess={handleSaveSuccess} />
        </div>

        {/* Right Column - Pricing Preview */}
        <div className="space-y-6">
          <PricingPreview />
        </div>
      </div>
    </div>
  );
};

export default PricingManagement;
